import { clamp, lerp } from '../core/math';
import type { DayNumber } from '../core/time';
import { T_MAX, T_MIN, inWinter } from './constants';
import type { Region } from './constants';
import type { Plan } from './types';

/**
 * 累計稼働日。`cw[0]=0`、`cw[i]` は `T_MIN + i − 1` の日までの合計。
 * 1 日の稼働係数 ＝ 稼働日/7 × (1 − 雨天・休止率) × （冬期なら冬期の稼働率）。
 */
export function buildWorkCurve(
  region: Region,
  p: Pick<Plan, 'days' | 'rain' | 'winterF'>,
): Float64Array {
  const n = T_MAX - T_MIN + 2;
  const cw = new Float64Array(n);
  for (let i = 1; i < n; i++) {
    const d = T_MIN + i - 1;
    let f = (p.days / 7) * (1 - p.rain / 100);
    if (inWinter(region, d)) f *= p.winterF / 100;
    cw[i] = cw[i - 1]! + f;
  }
  return cw;
}

/** 日 d の累計稼働日（線形補間。期間の外は端の値）。 */
export function workAt(cw: Float64Array, d: DayNumber): number {
  const last = cw.length - 1;
  const x = clamp(d - T_MIN, 0, last);
  const i = Math.floor(x);
  return i >= last ? cw[last]! : lerp(cw[i]!, cw[i + 1]!, x - i);
}

/** d0 から稼働日 need を積み上げた日。期間内に足りなければ、1 日 0.5 稼働日として先へのばす。 */
export function addWork(cw: Float64Array, d0: DayNumber, need: number): DayNumber {
  const base = workAt(cw, d0);
  let lo = d0;
  let hi = T_MAX;
  const avail = workAt(cw, hi) - base;
  if (avail < need) return hi + Math.ceil((need - avail) / 0.5);
  for (let k = 0; k < 40; k++) {
    const m = (lo + hi) / 2;
    if (workAt(cw, m) - base >= need) hi = m;
    else lo = m;
  }
  return Math.ceil(hi);
}
